import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  VehicleApi,
  type CreateVehicleProfileDto,
  type UpdateVehicleProfileDto,
} from '@/shared/api/vehicles'
import { useAuth } from '@/modules/auth'

export const vehiclesQueryKey = (ownerId: string | undefined) => ['vehicles', ownerId] as const

/**
 * The vehicles of `targetUserId`, or of the signed-in user when it's omitted.
 * Writes work only on your own vehicles. The server enforces this; the guard here
 * catches a page wiring up an edit control in visitor mode.
 */
export function useVehicles(targetUserId?: string) {
  const { user, getAccessToken, isAuthenticated } = useAuth()
  const queryClient = useQueryClient()

  const vehicleApi = useMemo(() => new VehicleApi(getAccessToken), [getAccessToken])

  const ownerId = targetUserId ?? user?.id
  const isOwner = !!user?.id && ownerId === user.id
  const queryKey = vehiclesQueryKey(ownerId)

  const {
    data: vehicles = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => (isOwner ? vehicleApi.getVehicles() : vehicleApi.getVehiclesForUser(ownerId as string)),
    enabled: isAuthenticated && !!ownerId,
  })

  const assertOwner = useCallback(() => {
    if (!isOwner) throw new Error("You can only change vehicles in your own garage")
  }, [isOwner])

  const invalidate = () => queryClient.invalidateQueries({ queryKey })

  const createMutation = useMutation({
    mutationFn: (vehicleData: CreateVehicleProfileDto) => {
      assertOwner()
      return vehicleApi.createVehicle(vehicleData)
    },
    onSuccess: invalidate,
  })

  const updateMutation = useMutation({
    mutationFn: ({ vehicleId, vehicleData }: { vehicleId: string; vehicleData: UpdateVehicleProfileDto }) => {
      assertOwner()
      return vehicleApi.updateVehicle(vehicleId, vehicleData)
    },
    onSuccess: invalidate,
  })

  const deleteMutation = useMutation({
    mutationFn: (vehicleId: string) => {
      assertOwner()
      return vehicleApi.deleteVehicle(vehicleId)
    },
    onSuccess: invalidate,
  })

  const setPrimaryMutation = useMutation({
    mutationFn: (vehicleId: string) => {
      assertOwner()
      return vehicleApi.setPrimaryVehicle(vehicleId)
    },
    onSuccess: invalidate,
  })

  const primaryVehicle = useMemo(
    () => vehicles.find((vehicle) => vehicle.is_primary) ?? vehicles[0] ?? null,
    [vehicles]
  )

  return {
    vehicles,
    primaryVehicle,
    isOwner,
    isLoading,
    error: error instanceof Error ? error.message : null,
    refetch,
    createVehicle: createMutation.mutateAsync,
    updateVehicle: (vehicleId: string, vehicleData: UpdateVehicleProfileDto) =>
      updateMutation.mutateAsync({ vehicleId, vehicleData }),
    deleteVehicle: deleteMutation.mutateAsync,
    setPrimaryVehicle: setPrimaryMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    isSettingPrimary: setPrimaryMutation.isPending,
  }
}
